import { useState, useEffect, useRef, useMemo } from 'react';

/**
 * Hard cap: if longer than maxChars, keeps (maxChars - charsToReplace) chars and appends "...".
 */
export function applyMaxChars(text, maxChars, charsToReplace = 3) {
  const safe = text || '';
  if (!maxChars || safe.length <= maxChars) return safe;
  const keep = maxChars - charsToReplace;
  if (keep <= 0) return '...'.slice(0, maxChars);
  return safe.substring(0, keep) + '...';
}

/**
 * Truncates text to fit within a single line, or applies maxChars + optional multiline wrapping.
 *
 * Backward compatible: omitting `maxChars` and `multiline` keeps the original width-based,
 * single-line ellipsis behavior (same as before those options existed).
 *
 * @param {string} text - The original text
 * @param {Object} options
 * @param {number} [options.charsToReplace=3] - Chars replaced by "..." when truncating by width or maxChars
 * @param {number} [options.maxChars] - Max character length of result (including "...")
 * @param {boolean} [options.multiline=false] - If true, only maxChars is applied; use CSS for word wrap (no width-based ellipsis)
 * @returns {{ textRef, containerRef, truncatedText }}
 */
export const useTextTruncate = (text, options = {}) => {
  const { charsToReplace = 3, maxChars, multiline = false } = options;
  const textRef = useRef(null);
  const containerRef = useRef(null);

  const source = useMemo(
    () => applyMaxChars(text || '', maxChars, charsToReplace),
    [text, maxChars, charsToReplace]
  );

  const [fitText, setFitText] = useState(source);

  useEffect(() => {
    if (multiline) {
      return;
    }

    const truncateText = () => {
      if (!source) {
        setFitText('');
        return;
      }

      if (!textRef.current) {
        setFitText(source);
        return;
      }

      const element = textRef.current;
      const container = containerRef.current || element.parentElement;

      if (!container) {
        setFitText(source);
        return;
      }

      element.textContent = source;
      const containerWidth = container.offsetWidth;
      const textWidth = element.scrollWidth;

      if (textWidth <= containerWidth) {
        setFitText(source);
        return;
      }

      let start = 0;
      let end = source.length - charsToReplace;
      let bestLength = 0;

      while (start <= end) {
        const mid = Math.floor((start + end) / 2);
        const truncated = source.substring(0, mid) + '...';

        element.textContent = truncated;
        const truncatedWidth = element.scrollWidth;

        if (truncatedWidth <= containerWidth) {
          bestLength = mid;
          start = mid + 1;
        } else {
          end = mid - 1;
        }
      }

      const finalText =
        bestLength > 0 ? source.substring(0, bestLength) + '...' : '...';

      setFitText(finalText);
    };

    const timeoutId = setTimeout(() => {
      truncateText();
    }, 0);

    const handleResize = () => {
      truncateText();
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver;
    const container = containerRef.current || textRef.current?.parentElement;
    if (container) {
      resizeObserver = new ResizeObserver(() => {
        truncateText();
      });
      resizeObserver.observe(container);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [source, charsToReplace, multiline]);

  const truncatedText = multiline ? source : fitText;

  return { textRef, containerRef, truncatedText };
};

export default useTextTruncate;
